import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import "./SupplierDashboard.css";

import supplierPortalService from "../../services/supplierPortalService";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthName = (value) =>
  monthNames[Number(value) - 1] || "";

const statementNumber = (item) =>
  `ST-${item.statement_year}-${String(
    item.statement_month
  ).padStart(2, "0")}`;

const getSupplierPhoto = (profile) =>
  profile?.profile_photo_url ||
  profile?.profile_photo ||
  profile?.photo_url ||
  profile?.photo ||
  profile?.image_url ||
  profile?.image ||
  "";

const documentStatus = (item) => {
  if (!item?.file_url) {
    return "Not Available";
  }

  if (!item?.expiry_date) {
    return "Available";
  }

  const today = new Date();
  const expiry = new Date(item.expiry_date);

  return expiry < today
    ? "Expired"
    : "Available";
};

const dashboardDocumentNames = [
  "RC",
  "Permit",
  "Fitness",
  "Insurance",
];

const SupplierDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [supplier, setSupplier] = useState(null);
  const [statements, setStatements] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [selectedVehicle, setSelectedVehicle] =
    useState("");

  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] =
    useState("");

  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] =
    useState("");

  const [documents, setDocuments] =
    useState([]);

  const [selectedVehicleInfo, setSelectedVehicleInfo] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] =
    useState(false);

  const [errorMessage, setErrorMessage] =
    useState("");

  const [sidebarOpen, setSidebarOpen] =
    useState(false);

  const [profileMenuOpen, setProfileMenuOpen] =
    useState(false);

  const [profilePhotoError, setProfilePhotoError] =
    useState(false);

  const profileMenuRef = useRef(null);

  const supplierPhoto = useMemo(
    () => getSupplierPhoto(supplier),
    [supplier]
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(
          event.target
        )
      ) {
        setProfileMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    document.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );

      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, []);

  useEffect(() => {
    setProfilePhotoError(false);
  }, [supplierPhoto]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      if (!supplierPortalService.isLoggedIn()) {
        navigate("/supplier/login", {
          replace: true,
        });
        return;
      }

      const [
        profileResponse,
        statementsResponse,
        vehiclesResponse,
      ] = await Promise.all([
        supplierPortalService.getMe(),
        supplierPortalService.getStatements(),
        supplierPortalService.getVehicles(),
      ]);

      const profile =
        profileResponse?.supplier || null;

      const statementList =
        statementsResponse?.statements || [];

      const vehicleList =
        vehiclesResponse?.vehicles || [];

      setSupplier(profile);
      setStatements(statementList);
      setVehicles(vehicleList);

      if (profile) {
        localStorage.setItem(
          "supplier_profile",
          JSON.stringify(profile)
        );
      }

      if (vehicleList.length) {
        setSelectedVehicle(
          String(vehicleList[0].id)
        );
      }
    } catch (error) {
      console.error(
        "Supplier dashboard error:",
        error
      );

      if (!supplierPortalService.isLoggedIn()) {
        navigate("/supplier/login", {
          replace: true,
        });
        return;
      }

      setErrorMessage(
        error?.message ||
          "Unable to load supplier dashboard."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (!selectedVehicle) {
      setYears([]);
      setSelectedYear("");
      setMonths([]);
      setSelectedMonth("");
      setDocuments([]);
      setSelectedVehicleInfo(null);
      return;
    }

    const loadYears = async () => {
      try {
        setErrorMessage("");
        setDocuments([]);
        setMonths([]);
        setSelectedMonth("");

        const response =
          await supplierPortalService.getVehicleYears(
            selectedVehicle
          );

        const list = response?.years || [];

        setYears(list);

        setSelectedYear(
          list.length
            ? String(list[0])
            : ""
        );

        const info = vehicles.find(
          (item) =>
            String(item.id) ===
            String(selectedVehicle)
        );

        setSelectedVehicleInfo(
          info || null
        );
      } catch (error) {
        setErrorMessage(
          error?.message ||
            "Unable to load vehicle years."
        );
      }
    };

    loadYears();
  }, [selectedVehicle, vehicles]);

  useEffect(() => {
    if (!selectedVehicle || !selectedYear) {
      setMonths([]);
      setSelectedMonth("");
      return;
    }

    const loadMonths = async () => {
      try {
        setDocuments([]);
        setSelectedMonth("");

        const response =
          await supplierPortalService.getVehicleMonths(
            selectedVehicle,
            selectedYear
          );

        const list = response?.months || [];

        setMonths(list);

        setSelectedMonth(
          list.length
            ? String(list[0])
            : ""
        );
      } catch (error) {
        setErrorMessage(
          error?.message ||
            "Unable to load vehicle months."
        );
      }
    };

    loadMonths();
  }, [selectedVehicle, selectedYear]);

  useEffect(() => {
    if (
      !selectedVehicle ||
      !selectedYear ||
      !selectedMonth
    ) {
      setDocuments([]);
      return;
    }

    const loadDocuments = async () => {
      try {
        setDocumentsLoading(true);
        setErrorMessage("");

        const response =
          await supplierPortalService.getVehicleDocuments(
            selectedVehicle,
            selectedYear,
            selectedMonth
          );

        setDocuments(
          response?.documents || []
        );

        setSelectedVehicleInfo(
          response?.vehicle || null
        );
      } catch (error) {
        setDocuments([]);

        setErrorMessage(
          error?.message ||
            "Unable to load vehicle documents."
        );
      } finally {
        setDocumentsLoading(false);
      }
    };

    loadDocuments();
  }, [
    selectedVehicle,
    selectedYear,
    selectedMonth,
  ]);

  const recentStatements = useMemo(
    () => statements.slice(0, 4),
    [statements]
  );

  const availableStatementFiles = useMemo(
    () =>
      statements.filter(
        (item) => Boolean(item.file_url)
      ).length,
    [statements]
  );

  const availableVehicleDocuments = useMemo(
    () =>
      documents.filter(
        (item) => Boolean(item.file_url)
      ).length,
    [documents]
  );

  const dashboardDocuments = useMemo(() => {
    return dashboardDocumentNames
      .map((name) =>
        documents.find(
          (document) =>
            String(document.label)
              .trim()
              .toLowerCase() ===
            name.toLowerCase()
        )
      )
      .filter(Boolean);
  }, [documents]);

  const handleLogout = () => {
    supplierPortalService.logout();

    navigate("/supplier/login", {
      replace: true,
    });
  };

  const openDocument = (item) => {
    if (!item?.file_url) return;

    window.open(
      item.file_url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openStatement = (item) => {
    if (!item?.file_url) return;

    window.open(
      item.file_url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const goTo = (path) => {
    setSidebarOpen(false);
    setProfileMenuOpen(false);

    navigate(path);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  if (loading) {
    return (
      <div className="supplier-dashboard">
        <div className="supplier-dashboard-loading">
          <div className="supplier-loading-spinner" />
          <p>
            Loading supplier dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="supplier-dashboard">

      {sidebarOpen && (
        <div
          className="supplier-sidebar-overlay"
          onClick={() =>
            setSidebarOpen(false)
          }
        />
      )}

      <aside
        className={`supplier-sidebar ${
          sidebarOpen ? "open" : ""
        }`}
      >
        <div className="supplier-sidebar-brand">
          <div className="supplier-brand-icon">
            🚌
          </div>

          <div>
            <strong>
              Fleet Management Pro
            </strong>

            <span>
              SUPPLIER PORTAL
            </span>
          </div>
        </div>

        <nav className="supplier-sidebar-nav">

          <button
            type="button"
            className={`supplier-nav-item ${
              isActive(
                "/supplier/dashboard"
              )
                ? "active"
                : ""
            }`}
            onClick={() =>
              goTo("/supplier/dashboard")
            }
          >
            <span className="nav-icon">
              ⌂
            </span>

            <span>
              Dashboard
            </span>
          </button>

          <button
            type="button"
            className={`supplier-nav-item ${
              isActive(
                "/supplier/vehicles"
              )
                ? "active"
                : ""
            }`}
            onClick={() =>
              goTo("/supplier/vehicles")
            }
          >
            <span className="nav-icon">
              ▣
            </span>

            <span>
              My Vehicles
            </span>
          </button>

          <button
            type="button"
            className={`supplier-nav-item ${
              isActive(
                "/supplier/vehicle-documents"
              )
                ? "active"
                : ""
            }`}
            onClick={() =>
              goTo(
                "/supplier/vehicle-documents"
              )
            }
          >
            <span className="nav-icon">
              ▤
            </span>

            <span>
              Vehicle Documents
            </span>
          </button>

          <button
            type="button"
            className={`supplier-nav-item ${
              isActive(
                "/supplier/statements"
              )
                ? "active"
                : ""
            }`}
            onClick={() =>
              goTo(
                "/supplier/statements"
              )
            }
          >
            <span className="nav-icon">
              ▥
            </span>

            <span>
              Statements
            </span>
          </button>

        </nav>
      </aside>

      <div className="supplier-main">

        <header className="supplier-topbar">

          <div className="supplier-topbar-left">
            <button
              type="button"
              className="supplier-menu-btn"
              aria-label="Open navigation"
              onClick={() =>
                setSidebarOpen(
                  (value) => !value
                )
              }
            >
              ☰
            </button>

            <div className="mobile-brand">
              <strong>
                Fleet Management Pro
              </strong>

              <span>
                SUPPLIER PORTAL
              </span>
            </div>
          </div>

          <div className="supplier-topbar-right">

            <div
              className="supplier-profile-menu"
              ref={profileMenuRef}
            >
              <button
                type="button"
                className={`supplier-user-trigger ${
                  profileMenuOpen
                    ? "open"
                    : ""
                }`}
                aria-label="Open supplier profile"
                aria-expanded={
                  profileMenuOpen
                }
                onClick={() =>
                  setProfileMenuOpen(
                    (value) => !value
                  )
                }
              >
                {supplierPhoto &&
                !profilePhotoError ? (
                  <img
                    src={supplierPhoto}
                    alt={
                      supplier?.supplier_name ||
                      "Supplier"
                    }
                    className="supplier-user-photo"
                    onError={() =>
                      setProfilePhotoError(
                        true
                      )
                    }
                  />
                ) : (
                  <span className="supplier-user-fallback">
                    {supplier?.supplier_name
                      ?.charAt(0)
                      ?.toUpperCase() ||
                      "S"}
                  </span>
                )}

                <span className="supplier-user-chevron">
                  {profileMenuOpen
                    ? "⌃"
                    : "⌄"}
                </span>
              </button>

              {profileMenuOpen && (
                <div className="supplier-profile-popover">

                  <div className="supplier-popover-head">

                    <div className="supplier-popover-avatar">
                      {supplierPhoto &&
                      !profilePhotoError ? (
                        <img
                          src={supplierPhoto}
                          alt={
                            supplier?.supplier_name ||
                            "Supplier"
                          }
                          onError={() =>
                            setProfilePhotoError(
                              true
                            )
                          }
                        />
                      ) : (
                        <span>
                          {supplier?.supplier_name
                            ?.charAt(0)
                            ?.toUpperCase() ||
                            "S"}
                        </span>
                      )}
                    </div>

                    <div className="supplier-popover-identity">
                      <strong>
                        {supplier?.supplier_name ||
                          "Supplier"}
                      </strong>

                      <span>
                        Supplier Account
                      </span>
                    </div>

                  </div>

                  <div className="supplier-popover-divider" />

                  <div className="supplier-popover-details">

                    <div className="supplier-popover-detail">
                      <span>
                        Mobile
                      </span>

                      <strong>
                        {supplier?.mobile ||
                          "Not available"}
                      </strong>
                    </div>

                    <div className="supplier-popover-detail">
                      <span>
                        Supplier Code
                      </span>

                      <strong>
                        {supplier?.supplier_code ||
                          "Not available"}
                      </strong>
                    </div>

                    {supplier?.email && (
                      <div className="supplier-popover-detail">
                        <span>
                          Email
                        </span>

                        <strong>
                          {supplier.email}
                        </strong>
                      </div>
                    )}

                  </div>

                  <button
                    type="button"
                    className="supplier-profile-logout"
                    onClick={handleLogout}
                  >
                    <span>↪</span>
                    Logout
                  </button>

                </div>
              )}
            </div>

          </div>
        </header>

        <main className="supplier-dashboard-content">

          {errorMessage && (
            <div className="supplier-dashboard-error">
              {errorMessage}
            </div>
          )}

          <section className="supplier-summary-grid">

            <div className="supplier-summary-card blue">
              <div className="summary-icon">
                ▣
              </div>

              <div className="summary-content">
                <span>
                  Assigned Vehicles
                </span>

                <strong>
                  {vehicles.length}
                </strong>

                <small>
                  Total vehicles assigned
                </small>
              </div>
            </div>

            <div className="supplier-summary-card green">
              <div className="summary-icon">
                ✓
              </div>

              <div className="summary-content">
                <span>
                  Vehicle Documents
                </span>

                <strong>
                  {availableVehicleDocuments}
                </strong>

                <small>
                  Available documents
                </small>
              </div>
            </div>

            <div className="supplier-summary-card purple">
              <div className="summary-icon">
                ▰
              </div>

              <div className="summary-content">
                <span>
                  Total Statements
                </span>

                <strong>
                  {statements.length}
                </strong>

                <small>
                  Generated statements
                </small>
              </div>
            </div>

            <div className="supplier-summary-card orange">
              <div className="summary-icon">
                ▤
              </div>

              <div className="summary-content">
                <span>
                  Statement Files
                </span>

                <strong>
                  {availableStatementFiles}
                </strong>

                <small>
                  Available files
                </small>
              </div>
            </div>

          </section>

          <section className="supplier-vehicle-card">

            <div className="supplier-section-header vehicle-header">

              <div className="section-title-wrap">

                <div className="section-icon">
                  ▤
                </div>

                <div>
                  <h2>
                    Vehicle &amp; Documents
                  </h2>

                  <p>
                    Select your vehicle and
                    period to view document
                    status.
                  </p>
                </div>

              </div>

            </div>

            <div className="supplier-vehicle-filters">

              <div className="supplier-filter-field">
                <label>
                  VEHICLE
                </label>

                <select
                  value={selectedVehicle}
                  onChange={(event) =>
                    setSelectedVehicle(
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    Select Vehicle
                  </option>

                  {vehicles.map(
                    (vehicle) => (
                      <option
                        key={vehicle.id}
                        value={vehicle.id}
                      >
                        {vehicle.vehicle_label ||
                          vehicle.vehicle_registration_number}
                      </option>
                    )
                  )}
                </select>
              </div>

              <div className="supplier-filter-field">
                <label>
                  YEAR
                </label>

                <select
                  value={selectedYear}
                  onChange={(event) =>
                    setSelectedYear(
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    Select Year
                  </option>

                  {years.map((item) => (
                    <option
                      key={item}
                      value={item}
                    >
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div className="supplier-filter-field">
                <label>
                  MONTH
                </label>

                <select
                  value={selectedMonth}
                  onChange={(event) =>
                    setSelectedMonth(
                      event.target.value
                    )
                  }
                >
                  <option value="">
                    Select Month
                  </option>

                  {months.map((item) => (
                    <option
                      key={item}
                      value={item}
                    >
                      {monthName(item)}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            {selectedVehicleInfo && (
              <div className="supplier-selected-vehicle">

                <div>
                  <span>
                    SELECTED VEHICLE
                  </span>

                  <strong>
                    {selectedVehicleInfo.vehicle_registration_number ||
                      selectedVehicleInfo.vehicle_label ||
                      "—"}
                  </strong>
                </div>

                <div>
                  <span>
                    MODEL
                  </span>

                  <strong>
                    {selectedVehicleInfo.vehicle_model ||
                      "—"}
                  </strong>
                </div>

                <div>
                  <span>
                    PERIOD
                  </span>

                  <strong>
                    {selectedMonth &&
                    selectedYear
                      ? `${monthName(
                          selectedMonth
                        )} ${selectedYear}`
                      : "—"}
                  </strong>
                </div>

              </div>
            )}

            {documentsLoading ? (
              <div className="supplier-documents-loading">
                <div className="supplier-loading-spinner small" />
                <span>
                  Loading vehicle documents...
                </span>
              </div>
            ) : dashboardDocuments.length ? (
              <div className="supplier-documents-grid">

                {dashboardDocuments.map(
                  (document) => {
                    const status =
                      documentStatus(
                        document
                      );

                    return (
                      <article
                        className="supplier-document-card"
                        key={document.key}
                      >
                        <div className="document-card-top">

                          <div className="supplier-document-icon">
                            {document.label ===
                            "RC"
                              ? "▤"
                              : document.label ===
                                "Permit"
                              ? "▥"
                              : document.label ===
                                "Fitness"
                              ? "✓"
                              : "▣"}
                          </div>

                          <div className="supplier-document-info">

                            <h3>
                              {document.label}
                            </h3>

                            <p>
                              {document.expiry_date
                                ? `Valid until ${document.expiry_date}`
                                : "Expiry date not available"}
                            </p>

                          </div>
                        </div>

                        <div className="document-card-bottom">

                          <span
                            className={`supplier-document-status ${status
                              .toLowerCase()
                              .replace(
                                " ",
                                "-"
                              )}`}
                          >
                            {status}
                          </span>

                          <button
                            type="button"
                            className="supplier-document-view-btn"
                            disabled={
                              !document.file_url
                            }
                            onClick={() =>
                              openDocument(
                                document
                              )
                            }
                          >
                            {document.file_url
                              ? "View"
                              : "Unavailable"}
                          </button>

                        </div>
                      </article>
                    );
                  }
                )}

              </div>
            ) : (
              <div className="supplier-document-empty">

                <div className="supplier-empty-icon">
                  ▤
                </div>

                <div>
                  <h3>
                    {selectedVehicle &&
                    selectedYear &&
                    selectedMonth
                      ? "No dashboard documents found"
                      : "Select vehicle, year and month"}
                  </h3>

                  <p>
                    Select an assigned vehicle
                    and period to see RC,
                    Permit, Fitness and
                    Insurance status.
                  </p>
                </div>

              </div>
            )}

            <button
              type="button"
              className="view-all-documents-btn"
              onClick={() =>
                goTo(
                  "/supplier/vehicle-documents"
                )
              }
            >
              View All Documents →
            </button>

          </section>

          <section className="supplier-recent-card">

            <div className="supplier-section-header">

              <div className="section-title-wrap">

                <div className="section-icon purple-icon">
                  ▥
                </div>

                <div>
                  <h2>
                    Recent Statements
                  </h2>

                  <p>
                    Your latest supplier
                    statements.
                  </p>
                </div>

              </div>

              <button
                type="button"
                className="view-all-btn"
                onClick={() =>
                  goTo(
                    "/supplier/statements"
                  )
                }
              >
                View All
              </button>

            </div>

            {recentStatements.length === 0 ? (
              <div className="supplier-empty-state">

                <div className="supplier-empty-icon">
                  ▤
                </div>

                <h3>
                  No statements available
                </h3>

                <p>
                  Your supplier statements
                  will appear here.
                </p>

              </div>
            ) : (
              <div className="supplier-table-wrap">

                <table className="supplier-table">

                  <thead>
                    <tr>
                      <th>
                        Statement No
                      </th>

                      <th>
                        Month
                      </th>

                      <th>
                        Year
                      </th>

                      <th>
                        Document
                      </th>

                      <th>
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {recentStatements.map(
                      (item) => (
                        <tr key={item.id}>

                          <td>
                            <strong>
                              {statementNumber(
                                item
                              )}
                            </strong>
                          </td>

                          <td>
                            {monthName(
                              item.statement_month
                            )}
                          </td>

                          <td>
                            {item.statement_year}
                          </td>

                          <td>
                            <span
                              className={
                                item.file_url
                                  ? "status-paid"
                                  : "status-pending"
                              }
                            >
                              {item.file_url
                                ? "Available"
                                : "Not Available"}
                            </span>
                          </td>

                          <td>
                            <button
                              type="button"
                              className="statement-view-btn"
                              disabled={
                                !item.file_url
                              }
                              onClick={() =>
                                openStatement(
                                  item
                                )
                              }
                            >
                              {item.file_url
                                ? "View"
                                : "Unavailable"}
                            </button>
                          </td>

                        </tr>
                      )
                    )}
                  </tbody>

                </table>

              </div>
            )}

          </section>

        </main>
      </div>
    </div>
  );
};

export default SupplierDashboard;